import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/appStore';
import { Trash2, Edit2, Plus } from 'lucide-react';

export default function CommentManager() {
  const { t } = useTranslation();
  const { comments, addComment, deleteComment, updateComment } = useAppStore();
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const handleAddComment = () => {
    if (newComment.trim()) {
      addComment({
        text: newComment,
        userName: `User ${comments.length + 1}`,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${Date.now()}`,
        timestamp: new Date().toLocaleString(),
      });
      setNewComment('');
    }
  };

  const handleEditComment = (id, text) => {
    setEditingId(id);
    setEditText(text);
  };

  const handleSaveEdit = (id) => {
    if (editText.trim()) {
      updateComment(id, { text: editText });
      setEditingId(null);
      setEditText('');
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-maroon">{t('dashboard.commentsList')}</h3>

      <div className="space-y-3">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={t('dashboard.commentText')}
          className="w-full bg-light border-2 border-primary/30 rounded-lg p-3 text-dark placeholder-dark/50 focus:outline-none focus:border-maroon transition resize-none"
          rows="3"
        />
        <button
          onClick={handleAddComment}
          className="flex items-center gap-2 w-full bg-gradient-to-r from-maroon to-primary hover:shadow-lg text-light font-semibold py-2 px-4 rounded-lg transition"
        >
          <Plus size={18} />
          {t('dashboard.addComment')}
        </button>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {comments.map((comment) => (
          <div
            key={comment.id}
            className="bg-light border-2 border-primary/30 hover:border-maroon rounded-lg p-4 transition"
          >
            <div className="flex items-start gap-3">
              <img
                src={comment.avatar}
                alt={comment.userName}
                className="w-10 h-10 rounded-full border border-primary"
              />
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-maroon">{comment.userName}</p>
                    <p className="text-xs text-dark/50">{comment.timestamp}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditComment(comment.id, comment.text)}
                      className="p-1 hover:bg-maroon/20 rounded transition text-maroon"
                      title={t('dashboard.editUser')}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => deleteComment(comment.id)}
                      className="p-1 hover:bg-red-500/20 rounded transition text-red-500"
                      title={t('dashboard.deleteUser')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {editingId === comment.id ? (
                  <div className="mt-3 space-y-2">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full bg-light border-2 border-primary/30 rounded p-2 text-dark text-sm resize-none"
                      rows="2"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(comment.id)}
                        className="flex-1 bg-maroon/20 hover:bg-maroon/30 text-maroon py-1 px-2 rounded text-sm transition font-semibold"
                      >
                        {t('dashboard.save')}
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="flex-1 bg-light border-2 border-primary/30 hover:border-maroon py-1 px-2 rounded text-sm transition"
                      >
                        {t('dashboard.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-dark/80">{comment.text}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
